import retry from "async-retry";
import { ActivityChangeType } from "@/components/ActivityStream/ActivityStreamItem";
import { setIFrameChanged } from "@/store/iframeSlice";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import {
  getAxiosErorrMessage,
  getUnexpiredToken,
  logAxiosError,
  useCanvasId,
} from "@/utils/utils";
import axios, { AxiosResponse } from "axios";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { useSession } from "@clerk/clerk-react";
import useActivityStore from "@/store/zustand/activityStore";
import { updateFromPersistedPages } from "@/store/zustand/pagesStore";

type SaveFileParams = {
  fileToContentsToSave: { [filename: string]: string };
  filenameToFileKey?: { [filename: string]: string };
  saveActivityType?: ActivityChangeType;
  onlyCreate?: boolean;
  retryCount?: number;
  abortController?: AbortController;
};

export const useSaveFile = () => {
  const canvasId = useCanvasId();

  const { session } = useSession();

  const triggerRefetchActivityStream = useActivityStore(
    (state) => state.triggerRefetchActivityStream,
  );

  const updateLookupsBasedOnPartialResponse = useCodebaseStore(
    (state) => state.updateLookupsBasedOnPartialResponse,
  );
  const setReloadFiles = useCodebaseStore((state) => state.setReloadFiles);

  const setFileToContents = useCodebaseStore(
    (state) => state.setFileToContents,
  );
  const fileToContents = useCodebaseStore((state) => state.fileToContents);

  const basePathToProject = useSelector(
    (state: any) => state.codebase.basePathToProject,
  );

  const dispatch = useDispatch();

  return async ({
    fileToContentsToSave,
    filenameToFileKey,
    saveActivityType,
    onlyCreate,
    retryCount,
    abortController,
  }: SaveFileParams): Promise<AxiosResponse> => {
    // If any fileToContents is null or undefined, remove it from the object, if it is an object, stringify it
    Object.keys(fileToContentsToSave).forEach((filename) => {
      if (
        fileToContentsToSave[filename] === null ||
        fileToContentsToSave[filename] === undefined
      ) {
        delete fileToContentsToSave[filename];
      } else if (typeof fileToContentsToSave[filename] === "object") {
        fileToContentsToSave[filename] = JSON.stringify(
          fileToContentsToSave[filename],
        );
      }
    });

    return new Promise(async (resolve, reject) => {
      let hasAllFallbackContents = true;
      const fallbackContents = {};
      Object.keys(fileToContentsToSave).forEach((filename) => {
        const fallback = fileToContents[filename];
        if (fallback) {
          fallbackContents[filename] = fallback;
        } else {
          hasAllFallbackContents = false;
        }
      });

      // Update right away for user feedback, but only if there is a fallback value
      if (hasAllFallbackContents) {
        Object.keys(fileToContentsToSave).forEach((filename) => {
          setFileToContents({
            key: filename,
            contents: fileToContentsToSave[filename],
          });
        });
      }

      const supabaseJWT = await getUnexpiredToken(session);
      try {
        const result = await retry(
          async () => {
            return axios.post(
              `${
                import.meta.env.VITE_BACKEND_BASE_URL
              }/canvases/${canvasId}/file`,
              {
                fileToContents: fileToContentsToSave,
                saveActivityType,
                onlyCreate,
              },
              {
                headers: {
                  Authorization: `Bearer ${supabaseJWT}`,
                },
                signal: abortController?.signal,
              },
            );
          },
          {
            retries: retryCount || 3,
            factor: 2,
            minTimeout: 1000,
          },
        );

        if (result?.data) {
          updateLookupsBasedOnPartialResponse(result.data);
        }

        triggerRefetchActivityStream();

        const reloadedFiles = Object.keys(fileToContentsToSave).map(
          (filename) => filenameToFileKey?.[filename] || filename,
        );

        setReloadFiles({ files: reloadedFiles });

        // Update pages from loadAllFiles response (if pages data is included)
        if (result.data?.pages && basePathToProject) {
          updateFromPersistedPages(
            canvasId,
            result.data.pages,
            basePathToProject,
            result.data.tempobookPagesDir,
          );
        }

        // Notify the iframe changed
        dispatch(setIFrameChanged());
        resolve(result);
      } catch (err) {
        logAxiosError("Error saving file", err);
        toast.error("Could not save file: " + getAxiosErorrMessage(err));

        Object.keys(fallbackContents).forEach((filename) => {
          setFileToContents({
            key: filename,
            contents: fallbackContents[filename],
          });
        });

        reject(err);
      }
    });
  };
};
